// Pagos a profesores — vista por mes con desglose por profesor.
// Muestra cálculo dinámico tentativo + snapshots reales si ya existen.

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, Loader2, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw, X, Wallet, FileDown, FileText,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listPagosProfesor, marcarPagadoProfesor, anularPagoProfesor, marcarPagoPendiente,
  descargarReciboProfesor, descargarModelo111,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function fmtEur(n) {
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function periodoActual() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function trimestreDePeriodo(periodo) {
  const [y, m] = periodo.split('-').map(Number);
  const t = Math.ceil(m / 3);
  return `${y}-T${t}`;
}

function periodoOffset(periodo, delta) {
  const [y, m] = periodo.split('-').map(Number);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function nombrePeriodo(periodo, lang) {
  const [y, m] = periodo.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  const localeMap = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const locale = localeMap[lang] || 'es-ES';
  const f = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  return f.charAt(0).toLocaleUpperCase() + f.slice(1);
}

export default function AdminPagosProfesor() {
  const { t, i18n } = useTranslation();
  const [periodo, setPeriodo] = useState(periodoActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accion, setAccion] = useState(null); // {tipo, fila, profesor}
  const [busy, setBusy] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listPagosProfesor({ periodo });
      setData(r);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [periodo]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtrarFilas = (filas) => {
    if (filtroEstado === 'todos') return filas;
    return filas.filter((f) => f.estado === filtroEstado);
  };

  const ejecutarAccion = async () => {
    if (!accion) return;
    setBusy(true);
    try {
      if (accion.tipo === 'marcar-pagado') {
        await marcarPagadoProfesor({
          sesion_id: accion.fila.sesion_id,
          referencia_pago: accion.referencia_pago || null,
          factura_recibida: !!accion.factura_recibida,
        });
      } else if (accion.tipo === 'anular') {
        await anularPagoProfesor(accion.fila.pago_id);
      } else if (accion.tipo === 'marcar-pendiente') {
        await marcarPagoPendiente(accion.fila.pago_id);
      }
      setAccion(null);
      await cargar();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e.message || t('admin.error_generico'));
      alert(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.pagos_profesor.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.pagos_profesor.sub')}</p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={async () => {
            const trim = trimestreDePeriodo(periodo);
            try { await descargarModelo111(trim); }
            catch (e) { alert(e.message || t('admin.error_generico')); }
          }}
        >
          <FileText size={16} strokeWidth={1.5} />
          {t('admin.pagos_profesor.descargar_modelo_111', { trimestre: trimestreDePeriodo(periodo).replace('-T', ' · T') })}
        </button>
      </header>

      <div className="pagos-periodo-bar">
        <button type="button" className="btn-icon" onClick={() => setPeriodo(periodoOffset(periodo, -1))} aria-label={t('admin.pagos_profesor.mes_anterior')}>
          <ChevronLeft size={18} />
        </button>
        <div className="pagos-periodo-label">
          <span className="pagos-periodo-nombre">{nombrePeriodo(periodo, i18n.language)}</span>
          <span className="pagos-periodo-iso">{periodo}</span>
        </div>
        <button type="button" className="btn-icon" onClick={() => setPeriodo(periodoOffset(periodo, 1))} aria-label={t('admin.pagos_profesor.mes_siguiente')}>
          <ChevronRight size={18} />
        </button>
        <button type="button" className="btn-secondary btn-mini" onClick={() => setPeriodo(periodoActual())}>
          {t('admin.pagos_profesor.hoy')}
        </button>
      </div>

      <div className="admin-filtros admin-filtros-inline">
        <label className="admin-filtro-campo">
          <span>{t('admin.pagos_profesor.filtro_estado')}</span>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="todos">{t('admin.pagos_profesor.estados.todos')}</option>
            <option value="pendiente">{t('admin.pagos_profesor.estados.pendiente')}</option>
            <option value="pagado">{t('admin.pagos_profesor.estados.pagado')}</option>
            <option value="cancelado">{t('admin.pagos_profesor.estados.cancelado')}</option>
          </select>
        </label>
      </div>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && data && (
        <>
          <div className="pagos-resumen">
            <div className="pagos-kpi">
              <span className="pagos-kpi-label">{t('admin.pagos_profesor.kpi.sesiones')}</span>
              <span className="pagos-kpi-valor">{data.total_sesiones}</span>
            </div>
            <div className="pagos-kpi">
              <span className="pagos-kpi-label">{t('admin.pagos_profesor.kpi.profesores')}</span>
              <span className="pagos-kpi-valor">{data.total_profesores}</span>
            </div>
            <div className="pagos-kpi">
              <span className="pagos-kpi-label">{t('admin.pagos_profesor.kpi.bruto')}</span>
              <span className="pagos-kpi-valor">{fmtEur(data.total_bruto)} €</span>
            </div>
            <div className="pagos-kpi">
              <span className="pagos-kpi-label">{t('admin.pagos_profesor.kpi.irpf')}</span>
              <span className="pagos-kpi-valor pagos-kpi-irpf">−{fmtEur(data.total_irpf)} €</span>
            </div>
            <div className="pagos-kpi pagos-kpi-neto">
              <span className="pagos-kpi-label">{t('admin.pagos_profesor.kpi.neto')}</span>
              <span className="pagos-kpi-valor">{fmtEur(data.total_neto)} €</span>
            </div>
          </div>

          {data.profesores.length === 0 && (
            <div className="admin-empty">{t('admin.pagos_profesor.vacio')}</div>
          )}

          {data.profesores.map((prof) => {
            const filas = filtrarFilas(prof.sesiones);
            if (filas.length === 0) return null;
            return (
              <ProfesorCard
                key={prof.profesor_id}
                profesor={prof}
                filas={filas}
                periodo={periodo}
                onAccion={setAccion}
              />
            );
          })}
        </>
      )}

      {accion && (
        <ModalAccion
          accion={accion}
          onChange={(patch) => setAccion((a) => ({ ...a, ...patch }))}
          onCerrar={() => !busy && setAccion(null)}
          onConfirmar={ejecutarAccion}
          busy={busy}
        />
      )}
    </div>
  );
}

function ProfesorCard({ profesor, filas, periodo, onAccion }) {
  const { t } = useTranslation();
  const [descargando, setDescargando] = useState(false);
  const regimenLabel = profesor.regimen_fiscal
    ? t('admin.pagos_profesor.regimen.' + profesor.regimen_fiscal)
    : t('admin.pagos_profesor.regimen.sin_configurar');

  const onDescargarRecibo = async () => {
    setDescargando(true);
    try { await descargarReciboProfesor(profesor.profesor_id, periodo); }
    catch (e) { alert(e.message || t('admin.error_generico')); }
    finally { setDescargando(false); }
  };

  return (
    <div className="pagos-profesor-card">
      <div className="pagos-profesor-head">
        <div>
          <h2 className="pagos-profesor-nombre">{profesor.nombre_publico || '—'}</h2>
          <p className="pagos-profesor-fiscal">
            <span>{regimenLabel}</span>
            {profesor.regimen_fiscal === 'con_irpf' && profesor.tipo_irpf != null && (
              <span> · IRPF {profesor.tipo_irpf}%</span>
            )}
            {profesor.nif && <span> · NIF {profesor.nif}</span>}
          </p>
          <button
            type="button"
            className="btn-mini btn-secondary"
            onClick={onDescargarRecibo}
            disabled={descargando}
            style={{ marginTop: 8 }}
          >
            {descargando ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />}
            {t('admin.pagos_profesor.descargar_recibo')}
          </button>
        </div>
        <div className="pagos-profesor-totales">
          <div>
            <span className="dim">{t('admin.pagos_profesor.kpi.bruto')}</span>
            <strong>{fmtEur(profesor.total_bruto)} €</strong>
          </div>
          <div>
            <span className="dim">{t('admin.pagos_profesor.kpi.irpf')}</span>
            <strong className="pagos-kpi-irpf">−{fmtEur(profesor.total_irpf)} €</strong>
          </div>
          <div className="pagos-profesor-neto">
            <span className="dim">{t('admin.pagos_profesor.kpi.neto')}</span>
            <strong>{fmtEur(profesor.total_neto)} €</strong>
          </div>
        </div>
      </div>

      <table className="admin-table pagos-tabla">
        <thead>
          <tr>
            <th>{t('admin.pagos_profesor.col.fecha')}</th>
            <th>{t('admin.pagos_profesor.col.clase')}</th>
            <th className="num">{t('admin.pagos_profesor.col.bruto')}</th>
            <th className="num">{t('admin.pagos_profesor.col.irpf')}</th>
            <th className="num">{t('admin.pagos_profesor.col.neto')}</th>
            <th>{t('admin.pagos_profesor.col.estado')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.sesion_id}>
              <td>{f.fecha_clase}{f.hora_inicio ? ` ${f.hora_inicio.slice(0, 5)}` : ''}</td>
              <td>
                {f.clase_tipo_nombre || '—'}
                {f.es_substitucion && (
                  <span className="estado-pill" style={{ marginLeft: 8 }}>
                    {t('admin.pagos_profesor.subst')}
                  </span>
                )}
              </td>
              <td className="num">{fmtEur(f.bruto)} €</td>
              <td className="num pagos-kpi-irpf">{f.irpf_retenido > 0 ? `−${fmtEur(f.irpf_retenido)} €` : '—'}</td>
              <td className="num"><strong>{fmtEur(f.neto)} €</strong></td>
              <td>
                <EstadoPill fila={f} />
                {f.sin_tarifa && <span className="warn-pill">{t('admin.pagos_profesor.warn.sin_tarifa')}</span>}
                {f.sin_datos_fiscales && <span className="warn-pill">{t('admin.pagos_profesor.warn.sin_fiscal')}</span>}
              </td>
              <td className="admin-acciones">
                {f.estado === 'pendiente' && !f.sin_tarifa && !f.sin_datos_fiscales && (
                  <button
                    type="button"
                    className="btn-mini btn-primary"
                    onClick={() => onAccion({
                      tipo: 'marcar-pagado',
                      fila: f,
                      profesor,
                      referencia_pago: '',
                      factura_recibida: false,
                    })}
                    aria-label={t('admin.pagos_profesor.accion.marcar_pagado')}
                  >
                    <CheckCircle2 size={14} /> {t('admin.pagos_profesor.accion.marcar_pagado_short')}
                  </button>
                )}
                {f.estado === 'pagado' && (
                  <button
                    type="button"
                    className="btn-mini btn-secondary"
                    onClick={() => onAccion({ tipo: 'marcar-pendiente', fila: f, profesor })}
                  >
                    <RotateCcw size={14} /> {t('admin.pagos_profesor.accion.deshacer')}
                  </button>
                )}
                {f.estado !== 'cancelado' && f.pago_id && (
                  <button
                    type="button"
                    className="btn-mini"
                    onClick={() => onAccion({ tipo: 'anular', fila: f, profesor })}
                  >
                    <X size={14} /> {t('admin.pagos_profesor.accion.anular')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EstadoPill({ fila }) {
  const { t } = useTranslation();
  const cls = {
    pagado: 'estado-pill estado-pill-ok',
    pendiente: 'estado-pill',
    cancelado: 'estado-pill estado-pill-err',
  }[fila.estado] || 'estado-pill';
  return <span className={cls}>{t('admin.pagos_profesor.estados.' + fila.estado)}</span>;
}

function ModalAccion({ accion, onChange, onCerrar, onConfirmar, busy }) {
  const { t } = useTranslation();
  const fila = accion.fila;
  const tit = {
    'marcar-pagado': t('admin.pagos_profesor.modal.titulo_pagar'),
    'marcar-pendiente': t('admin.pagos_profesor.modal.titulo_pendiente'),
    'anular': t('admin.pagos_profesor.modal.titulo_anular'),
  }[accion.tipo];

  return createPortal(
    <div className="modal-backdrop" onClick={onCerrar}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo"><Wallet size={20} style={{ marginRight: 8 }} /> {tit}</h2>

        <div className="pagos-modal-resumen">
          <div><span className="dim">{t('admin.pagos_profesor.modal.profesor')}</span><strong>{accion.profesor.nombre_publico}</strong></div>
          <div><span className="dim">{t('admin.pagos_profesor.modal.sesion')}</span><strong>{fila.fecha_clase} · {fila.clase_tipo_nombre || '—'}</strong></div>
          <div><span className="dim">{t('admin.pagos_profesor.modal.bruto')}</span><strong>{fmtEur(fila.bruto)} €</strong></div>
          <div><span className="dim">{t('admin.pagos_profesor.modal.irpf')}</span><strong>{fila.irpf_retenido > 0 ? `−${fmtEur(fila.irpf_retenido)} €` : '—'}</strong></div>
          <div><span className="dim">{t('admin.pagos_profesor.modal.neto')}</span><strong>{fmtEur(fila.neto)} €</strong></div>
        </div>

        {accion.tipo === 'marcar-pagado' && (
          <div className="admin-form-grid">
            <div className="modal-campo full">
              <label>{t('admin.pagos_profesor.modal.referencia')}</label>
              <input
                type="text"
                value={accion.referencia_pago || ''}
                onChange={(e) => onChange({ referencia_pago: e.target.value })}
                placeholder={t('admin.pagos_profesor.modal.referencia_placeholder')}
                maxLength={200}
              />
            </div>
            <div className="modal-campo full">
              <label className="admin-filtro-toggle">
                <input
                  type="checkbox"
                  checked={!!accion.factura_recibida}
                  onChange={(e) => onChange({ factura_recibida: e.target.checked })}
                />
                <span>{t('admin.pagos_profesor.modal.factura_recibida')}</span>
              </label>
            </div>
          </div>
        )}

        {accion.tipo !== 'marcar-pagado' && (
          <p className="admin-form-hint">
            {accion.tipo === 'anular'
              ? t('admin.pagos_profesor.modal.hint_anular')
              : t('admin.pagos_profesor.modal.hint_pendiente')}
          </p>
        )}

        <div className="modal-acciones">
          <button type="button" className="btn-secondary" onClick={onCerrar} disabled={busy}>
            {t('comun.cancelar')}
          </button>
          <button type="button" className="btn-primary" onClick={onConfirmar} disabled={busy}>
            {busy ? <Loader2 size={16} className="spin" /> : t('comun.confirmar')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
